import { api } from '@/lib/http-client';
import type {
  DropiCatalogoProducto,
  DropiEnvioResultado,
  DropiEspejoResultado,
  DropiGeografia,
  DropiHuella,
  DropiPrevisualizacion,
  DropiStatus,
  DropiStockResumen,
  DropiTestResult,
  SaveDropiPayload,
} from '@/types/dropi';

/**
 * Módulo Dropi (docs/DROPI.md) en la capa de API del frontend.
 *
 * El token NUNCA se devuelve (solo `previewToken`), y guardar hace MERGE: mandar solo
 * `{ activo: false }` apaga la integración sin borrar el token.
 */

/** `GET /api/tenants/:tenantId/dropi` — estado de la conexión. */
export function fetchDropiStatus(tenantId: string): Promise<DropiStatus> {
  return api.get<DropiStatus>(`/api/tenants/${tenantId}/dropi`);
}

/** `PUT /api/tenants/:tenantId/dropi` (solo ADMIN). */
export function saveDropiConfig(tenantId: string, payload: SaveDropiPayload): Promise<DropiStatus> {
  return api.put<DropiStatus>(`/api/tenants/${tenantId}/dropi`, payload);
}

/** `DELETE /api/tenants/:tenantId/dropi` (solo ADMIN). */
export function removeDropiConfig(tenantId: string): Promise<{ eliminado: boolean }> {
  return api.delete<{ eliminado: boolean }>(`/api/tenants/${tenantId}/dropi`);
}

/**
 * `POST /api/tenants/:tenantId/dropi/test` (solo ADMIN). Sin payload usa lo guardado; con
 * `{ token, origin }` prueba lo que el admin acaba de pegar, SIN guardar nada.
 */
export function testDropiConnection(
  tenantId: string,
  payload?: { token?: string; origin?: string },
): Promise<DropiTestResult> {
  return api.post<DropiTestResult>(`/api/tenants/${tenantId}/dropi/test`, payload ?? {});
}

/** `GET /api/tenants/:tenantId/dropi/productos` — catálogo de Dropi para vincular productos. */
export function fetchDropiProductos(
  tenantId: string,
  params: { startData?: number; pageSize?: number } = {},
): Promise<DropiCatalogoProducto[]> {
  const query = new URLSearchParams();
  if (params.startData) query.set('startData', String(params.startData));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  const qs = query.toString();
  return api.get<DropiCatalogoProducto[]>(
    `/api/tenants/${tenantId}/dropi/productos${qs ? `?${qs}` : ''}`,
  );
}

/**
 * `GET /api/tenants/:tenantId/dropi/productos/:productId` — UN producto del catálogo por su id de
 * Dropi. Es la vía rápida para vincular: el API de Dropi no busca por texto, así que tener el id
 * ahorra recorrer el catálogo paginado. 404 si Dropi no conoce ese id.
 */
export function fetchDropiProducto(
  tenantId: string,
  productId: number,
): Promise<DropiCatalogoProducto> {
  return api.get<DropiCatalogoProducto>(
    `/api/tenants/${tenantId}/dropi/productos/${productId}`,
  );
}

/** `GET .../dropi/geografia` — departamentos y (si se pide uno) sus ciudades. */
export function fetchDropiGeografia(
  tenantId: string,
  departamento?: string,
): Promise<DropiGeografia> {
  const qs = departamento ? `?departamento=${encodeURIComponent(departamento)}` : '';
  return api.get<DropiGeografia>(`/api/tenants/${tenantId}/dropi/geografia${qs}`);
}

/** `GET .../dropi/huella?telefono=&forzar=` — huella digital del cliente. */
export function fetchDropiHuella(
  tenantId: string,
  telefono: string,
  opciones: { meses?: number; forzar?: boolean } = {},
): Promise<DropiHuella> {
  const query = new URLSearchParams({ telefono });
  if (opciones.meses) query.set('meses', String(opciones.meses));
  if (opciones.forzar) query.set('forzar', 'true');
  return api.get<DropiHuella>(`/api/tenants/${tenantId}/dropi/huella?${query}`);
}

/** `GET .../orders/:orderId/dropi/previsualizacion` — qué se enviaría (sin enviarlo). */
export function fetchDropiPrevisualizacion(
  tenantId: string,
  orderId: string,
): Promise<DropiPrevisualizacion> {
  return api.get<DropiPrevisualizacion>(
    `/api/tenants/${tenantId}/orders/${orderId}/dropi/previsualizacion`,
  );
}

/** `POST .../orders/:orderId/dropi` (solo ADMIN) — crea la orden en Dropi. */
export function enviarOrdenADropi(
  tenantId: string,
  orderId: string,
  opciones: { reenviar?: boolean } = {},
): Promise<DropiEnvioResultado> {
  return api.post<DropiEnvioResultado>(
    `/api/tenants/${tenantId}/orders/${orderId}/dropi`,
    opciones,
  );
}

/** `POST .../orders/:orderId/dropi/sincronizar` (solo ADMIN) — trae el estado desde Dropi. */
export function sincronizarOrdenDropi(
  tenantId: string,
  orderId: string,
): Promise<DropiEspejoResultado> {
  return api.post<DropiEspejoResultado>(
    `/api/tenants/${tenantId}/orders/${orderId}/dropi/sincronizar`,
    {},
  );
}

/**
 * `POST .../dropi/stock/sincronizar` (solo ADMIN) — trae el STOCK real de Dropi de los productos
 * vinculados y lo guarda en cada producto (docs/DROPI.md).
 *
 * Es lo que hace que el bot deje de ofrecer lo agotado, así que el panel lo ofrece como botón y el
 * backend además lo corre solo cada `DROPI_STOCK_SYNC_MINUTOS` minutos.
 */
export function sincronizarStockDropi(tenantId: string): Promise<DropiStockResumen> {
  return api.post<DropiStockResumen>(`/api/tenants/${tenantId}/dropi/stock/sincronizar`, {});
}
